import { useMemo } from 'react';
import { Alert, Loader, Tabs } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../../api/client';
import type { LogbookScopeKind } from '../../api/types';
import { useSession } from '../../app/session';
import { PageHeader } from '../../components/PageHeader';
import { LogbookView } from './LogbookView';

/**
 * The club's logbooks in one place.
 *
 * Club, school and airport are the same records under three filters, and "my logbook" is the
 * signed-in pilot's own Part-FCL book. The school and airport tabs point at the nested
 * organisations, so a club without a school simply does not show that tab.
 */
export function LogbookPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/$org/logbook' });
  const { organisation, user } = useSession();
  const org = organisation!.slug;

  const detail = useQuery({
    queryKey: ['organisation', org],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/organisations/{slug}/', { params: { path: { slug: org } } }),
      ),
  });

  const school = useMemo(
    () => detail.data?.children?.find((child) => child.kind === 'FLIGHT_SCHOOL'),
    [detail.data],
  );
  const airport = useMemo(
    () => detail.data?.children?.find((child) => child.kind === 'AIRPORT'),
    [detail.data],
  );

  const personId = user?.person?.id ?? null;
  const scope = (search.scope ?? 'CLUB').toUpperCase() as LogbookScopeKind;

  const setScope = (value: string | null) =>
    void navigate({
      to: '/$org/logbook',
      params: { org },
      search: { scope: (value ?? 'CLUB').toLowerCase(), subject: undefined },
    });

  const subjectId = (() => {
    if (search.subject) return search.subject;
    if (scope === 'PILOT') return personId ?? undefined;
    if (scope === 'SCHOOL') return school?.id;
    if (scope === 'AIRPORT') return airport?.id;
    return detail.data?.id;
  })();

  return (
    <>
      <PageHeader title={t('logbook.title')} subtitle={organisation?.name} />

      <Tabs value={scope} onChange={setScope} mb="md">
        <Tabs.List>
          <Tabs.Tab value="CLUB">{t('logbook.CLUB')}</Tabs.Tab>
          {school ? <Tabs.Tab value="SCHOOL">{t('logbook.SCHOOL')}</Tabs.Tab> : null}
          {airport ? <Tabs.Tab value="AIRPORT">{t('logbook.AIRPORT')}</Tabs.Tab> : null}
          {personId ? <Tabs.Tab value="PILOT">{t('logbook.PILOT')}</Tabs.Tab> : null}
        </Tabs.List>
      </Tabs>

      {detail.isLoading ? (
        <Loader />
      ) : subjectId ? (
        <LogbookView key={`${scope}-${subjectId}`} kind={scope} subjectId={subjectId} />
      ) : (
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={17} />}>
          {scope === 'PILOT' ? t('logbook.noPersonLinked') : t('common.noResults')}
        </Alert>
      )}
    </>
  );
}
